import { Inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { UserService } from '@app/services/user/user.service';
import { defaultRoute } from '@utils/default-route';
import { Observable } from 'rxjs';
import { concatMap, map, switchMap, take } from 'rxjs/operators';
import { APP_CONFIG, AppConfiguration } from '@app/infrastructure/auth/app-configuration';
import { AccountService } from '@core/services/account/account.service';
import { OnboardingProductsEnum } from '@core/services/account/account.interface';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';

@Injectable({
	providedIn: 'root',
})
export class DefaultRouteRedirectGuard implements CanActivate {
	constructor(
		@Inject(APP_CONFIG)
		private appConfig: AppConfiguration,
		private userService: UserService,
		private featureFlagManager: FeatureFlagManagerService,
		private router: Router,
		private accountService: AccountService,
	) {}

	public canActivate(): Observable<UrlTree | boolean> {
		return this.findDefaultRoute();
	}

	/**
	 * 1. Verify isAuthenticated user or not > requestLogin if needed.
	 * 1.1. Update user model (by requesting user$ from userService)
	 * 2. Find defaultRoute for selected organization and navigate user to it
	 * @private
	 */
	private findDefaultRoute(): Observable<UrlTree | boolean> {
		return this.userService.isAuthenticated$.pipe(
			take(1),
			concatMap((isAuthenticated: boolean) => {
				if (!isAuthenticated) {
					return this.userService.requestLogin().pipe(map(() => true));
				}

				return this.userService.user$.pipe(
					take(1),
					switchMap(() => this.accountService.loadAccounts()),
					switchMap(() => this.featureFlagManager.flags$),
					take(1),
					map((flags) => {
						const accounts = this.accountService.getAccountsSnapshot();
						// if empty then navigate to onboarding
						const hasAccounts = accounts.length > 0;
						if (!hasAccounts) {
							return this.router.parseUrl(`/${AppRouteHelper.onboardAccount}`);
						}
						// if some account has Kompass enabled then skip home and navigate directly to Kompass
						const isKompassEnabled = accounts.some((account) =>
							account.product_permissions?.includes(OnboardingProductsEnum.Kompass),
						);
						if (isKompassEnabled) {
							return this.router.parseUrl(`/${AppRouteHelper.kompass}`);
						}

						const isOptimizationHubEnabled = flags[FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB];
						const url = defaultRoute(isOptimizationHubEnabled, this.appConfig.customerType);
						return this.router.parseUrl(`/${url}`);
					}),
				);
			}),
		);
	}
}
